import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { RiEyeLine, RiEyeOffLine, RiLoginBoxLine, RiErrorWarningLine } from 'react-icons/ri';
import authService from '../../services/authService';
import { resolveLandingRoute } from '../../config/navigation';
import { buildMenuAccessChecker } from '../../utils/permissions';

interface LoginFormProps {
  onSuccess?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const passwordRef = useRef<HTMLInputElement>(null);

  // BUG-AUTH-001-DBLLOGIN FIX: `disabled={isSubmitting}` ไม่กันคลิกที่ 2 ใน tick เดียวกัน —
  // state ต้องรอ re-render ก่อนปุ่มถึงจะ disabled จริง แต่ sync click handler ยิงไปแล้วทั้งสองครั้ง
  // → ได้ POST /auth/login 2 ครั้ง = refresh_token 2 แถว (duplicate session).
  // ref flip แบบ synchronous จึงเป็นตัวที่กันจริงที่ระดับ event (pattern เดียวกับ ConfirmModal)
  const inFlightRef = useRef(false);

  const { login, error: authError, clearError } = useAuth();
  const navigate = useNavigate();

  // Clear any stale auth errors only on INITIAL mount (not on re-render after failed login)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // Only clear if there's no recent login attempt (stale error from navigation)
      const hasRecentAttempt = sessionStorage.getItem('login_attempted');
      if (!hasRecentAttempt) {
        clearError();
      }
      sessionStorage.removeItem('login_attempted');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inFlightRef.current) return; // race-loser drop: มี login ค้างอยู่แล้ว
    clearError();
    setFieldErrors({});

    // Validate fields
    const errors: Record<string, string> = {};

    if (!username.trim()) {
      errors.username = 'กรุณากรอกชื่อผู้ใช้';
    }

    if (!password) {
      errors.password = 'กรุณากรอกรหัสผ่าน';
    }

    setHasAttemptedSubmit(true);

    // If there are validation errors, set them and focus on first error
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      // Focus on first error field
      const firstErrorField = Object.keys(errors)[0];
      const errorInput = document.getElementById(firstErrorField);
      if (errorInput) {
        (errorInput as HTMLInputElement).focus();
      }
      return;
    }

    inFlightRef.current = true;
    setIsSubmitting(true);
    sessionStorage.setItem('login_attempted', '1');

    try {
      await login(username, password);
    } catch {
      // AuthContext.login ปกติไม่ throw — กันกรณี network error หลุดออกมา ให้ไหลลง fail path ข้างล่าง
      // (ถ้าไม่ catch ref จะค้าง true และปุ่มจะกดไม่ติดอีกเลย)
    } finally {
      setIsSubmitting(false);
    }

    // AuthContext.login ไม่ throw — ตรวจสอบ authError หลัง await เสร็จ
    // ถ้า login สำเร็จ authError จะเป็น null และ isAuthenticated จะเป็น true
    // ถ้า login ล้มเหลว authError จะมีข้อความ error อยู่ใน context
    // ใช้ flag จาก localStorage เพื่อตรวจสอบว่า login สำเร็จหรือไม่
    const accessToken = localStorage.getItem('access_token');
    if (accessToken) {
      // login สำเร็จ — navigate ออก
      if (onSuccess) {
        onSuccess();
      } else {
        // G-26 (PO 2026-08-24): login แล้วไปหน้า landing แรกเสมอ — ยกเลิก restore fromState
        // (FINDING-S453-001 เดิม) เพราะจำหน้าค้างของผู้ใช้คนก่อนข้าม session ได้
        // P-S616-1 FIX: เลือกเมนูแรกที่ role เปิดได้จริง (dashboard อันดับแรกเสมอถ้ามีสิทธิ์)
        //
        // อ่านสิทธิ์จาก authService (localStorage) ไม่ใช่ context — authService.login เขียน
        // localStorage แบบ synchronous แล้ว แต่ state ของ AuthContext ยัง re-render ไม่เสร็จ
        // ในจังหวะนี้ ถ้าอ่านจาก context จะได้ค่าว่างของผู้ใช้คนก่อน
        const freshUser = authService.getCurrentUser();
        const canAccess = buildMenuAccessChecker(
          authService.getPermissions(),
          Boolean(freshUser?.is_superadmin || freshUser?.is_superuser)
        );
        navigate(resolveLandingRoute(canAccess), { replace: true });
      }
    } else {
      // login ล้มเหลว — ปลด guard ให้กรอกใหม่ได้ (สำเร็จ = ไม่ปลด เพราะกำลัง navigate ออกจากหน้านี้)
      inFlightRef.current = false;
      // clear เฉพาะ password เก็บ username ไว้ แล้ว focus กลับ
      setPassword('');
      setTimeout(() => passwordRef.current?.focus(), 0);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      {authError && (
        <div className="flex items-start gap-3 bg-danger-50 border border-danger-300 text-danger-700 px-4 py-3 rounded-lg" role="alert" aria-live="assertive">
          <RiErrorWarningLine className="w-5 h-5 mt-0.5 flex-shrink-0 text-danger-500" />
          <p className="text-sm font-medium">{authError}</p>
        </div>
      )}
      
      <div className="space-y-2">
        <label htmlFor="username" className="block text-sm font-medium text-secondary-700">
          ชื่อผู้ใช้
        </label>
        <input
          id="username"
          type="text"
          value={username}
          onChange={(e) => {
            setUsername(e.target.value);
            if (fieldErrors.username) {
              setFieldErrors(prev => ({ ...prev, username: '' }));
            }
          }}
          disabled={isSubmitting}
          aria-invalid={!!(hasAttemptedSubmit && fieldErrors.username)}
          aria-describedby={hasAttemptedSubmit && fieldErrors.username ? 'username-error' : undefined}
          className={`input w-full h-11 text-secondary-900 ${
            hasAttemptedSubmit && fieldErrors.username ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500' : ''
          }`}
          placeholder="กรอกชื่อผู้ใช้ของคุณ"
          autoComplete="username"
          autoFocus
        />
        {hasAttemptedSubmit && fieldErrors.username && (
          <p id="username-error" role="alert" className="mt-1 text-sm font-medium text-danger-600 flex items-center gap-1">
            <RiErrorWarningLine className="w-3.5 h-3.5 flex-shrink-0" />
            {fieldErrors.username}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="password" className="block text-sm font-medium text-secondary-700">
          รหัสผ่าน
        </label>
        <div className="relative">
          <input
            ref={passwordRef}
            id="password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (fieldErrors.password) {
                setFieldErrors(prev => ({ ...prev, password: '' }));
              }
            }}
            disabled={isSubmitting}
            aria-invalid={!!(hasAttemptedSubmit && fieldErrors.password)}
            aria-describedby={hasAttemptedSubmit && fieldErrors.password ? 'password-error' : undefined}
            className={`input w-full h-11 text-secondary-900 pr-11 ${
              hasAttemptedSubmit && fieldErrors.password ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500' : ''
            }`}
            placeholder="กรอกรหัสผ่านของคุณ"
            autoComplete="current-password"
            maxLength={128}
          />
          {/* NFR-AUTH-001 FIX: eye toggle hit area was 20×20px (icon-only, no padding) —
              below the 44×44 touch-target minimum (WCAG 2.5.8 AA = 24×24 · Apple HIG 44).
              Full-height (inset-y-0 = h-11 = 44px) × w-11 (44px) flex-centered so the icon
              stays visually right while the tap area meets the minimum. Input bumped pr-10→pr-11
              so typed text clears the wider button. */}
          <button
            type="button"
            onClick={togglePasswordVisibility}
            aria-label={showPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
            className="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-secondary-500 hover:text-secondary-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500/30"
          >
            {showPassword ? (
              <RiEyeOffLine className="w-5 h-5" />
            ) : (
              <RiEyeLine className="w-5 h-5" />
            )}
          </button>
        </div>
        {hasAttemptedSubmit && fieldErrors.password && (
          <p id="password-error" role="alert" className="mt-1 text-sm font-medium text-danger-600 flex items-center gap-1">
            <RiErrorWarningLine className="w-3.5 h-3.5 flex-shrink-0" />
            {fieldErrors.password}
          </p>
        )}
      </div>

      <div className="flex justify-end">
        <Link
          to="/forgot-password"
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ลืมรหัสผ่าน?
        </Link>
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full h-11 flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white rounded-md font-medium transition-colors duration-150 disabled:opacity-70 disabled:cursor-not-allowed"
      >
        {isSubmitting ? (
          <>
            <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
            กำลังเข้าสู่ระบบ...
          </>
        ) : (
          <>
            <RiLoginBoxLine className="w-5 h-5 mr-2" />
            เข้าสู่ระบบ
          </>
        )}
      </button>
      
      {/* Login hint removed for security */}
    </form>
  );
};

export default LoginForm;